// Phase 6.0a.1 — one-shot fetch of /setup/status on app boot.
//
// The viewer needs to know — BEFORE anything else renders —
// whether the service is in its first-run state (sync_users
// empty → setup wizard required) so it can either route
// straight to /setup or proceed with the normal /login flow.
//
// Polling cadence: ONE fetch per app session. After the first
// successful response, the state is held in memory for the rest
// of the session. After POST /setup succeeds, the markSetupDone()
// helper flips state to "not required" without a re-fetch (the
// new endpoint would still return false, so the round-trip is
// wasted). Refreshing the browser refetches naturally.
//
// Failure posture: if /setup/status itself fails (network error,
// 5xx, etc.), we fall through to setup_required=false and let
// the normal flow proceed. Rationale: a transient network blip
// shouldn't block legitimate users; the next /auth/login will
// reveal the real state. The alternative ("if you can't tell,
// assume setup needed") would mean the operator with valid
// credentials sees a setup form during a network blip and
// could accidentally try to re-bootstrap — worse UX.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { getSetupStatus } from "../lib/api";

export interface SetupStatusState {
  /** True while the initial /setup/status fetch is in flight.
   *  Routing should render a thin skeleton (not the whole app)
   *  while loading is true. */
  loading: boolean;
  /** True when sync_users is empty and POST /setup is required
   *  before any other route is usable. */
  setupRequired: boolean;
  /** Called by SetupPage after POST /setup succeeds. Flips
   *  setupRequired to false synchronously so the next render
   *  unblocks routing. No round-trip. */
  markSetupDone: () => void;
}

const Ctx = createContext<SetupStatusState | undefined>(undefined);

export interface SetupStatusProviderProps {
  children: ReactNode;
}

export function SetupStatusProvider({ children }: SetupStatusProviderProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [setupRequired, setSetupRequired] = useState<boolean>(false);
  const fetched = useRef(false);

  useEffect(() => {
    // useEffect can fire twice in StrictMode (dev only). Guard
    // against double-fetch.
    if (fetched.current) return;
    fetched.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const status = await getSetupStatus();
        if (!cancelled) setSetupRequired(status.setup_required);
      } catch {
        // Conservative posture: treat fetch failure as
        // "setup not required" so legitimate users with valid
        // credentials aren't blocked from logging in during a
        // network blip.
        if (!cancelled) setSetupRequired(false);
      } finally {
        // NOT gated on `cancelled`: in dev StrictMode the mount →
        // unmount → remount cycle runs cleanup (cancelled = true)
        // while the `fetched` ref guard stops the second mount from
        // re-fetching — so this finally is the ONLY place loading
        // can flip, and gating it left dev stuck on the splash
        // forever. A post-unmount setState is a no-op in React 18.
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const markSetupDone = useCallback(() => {
    setSetupRequired(false);
  }, []);

  const value = useMemo<SetupStatusState>(
    () => ({ loading, setupRequired, markSetupDone }),
    [loading, setupRequired, markSetupDone],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSetupStatus(): SetupStatusState {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error(
      "useSetupStatus must be used inside a SetupStatusProvider",
    );
  }
  return ctx;
}
